// The tour map (Advanced Virtual Tour): a small 3D diagram of the tour. Scans
// close together merge into one node, lines are the hops the rings allow, a
// dashed jump joins each area to its nearest neighbour, and storeys stack as
// plates. Gaps between areas are shortened so a far area does not shrink the
// rest to dots; each jump keeps its real length as a label.
//
// Heights: a storey is found from the floor under each scan (not the scanner,
// whose tripod height varies by a metre), storeys are drawn a clear gap apart,
// and within a storey heights stay true. Exaggerating every height (it was ×6
// on a large site) drew the steps up to a prayer hall as a floor above.

import { AREA_HOP_M, FLOOR_GAP_M, linkedPairs, type StationLink, type TourArea, type TourStation, type Vec3 } from './tourAreas';

export interface TourMapNode {
  id: string;
  areaKey: string;
  level: number;
  // Where the diagram draws it (gaps shortened) and where it really is.
  position: Vec3;
  real: Vec3;
  stationIds: string[];
}
export interface TourMapLink { a: string; b: string }
export interface TourMapJump extends TourMapLink { distance: number }
export interface TourMapPlate { areaKey: string; level: number; z: number; outline: Array<[number, number]> }
export interface TourMap {
  nodes: TourMapNode[];
  hops: TourMapLink[];
  jumps: TourMapJump[];
  plates: TourMapPlate[];
  // Node of each station.
  nodeOf: Map<string, string>;
}


const flat = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y);

function siteSpan(areas: TourArea[]): number {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const a of areas) for (const s of a.stations) {
    minX = Math.min(minX, s.position.x); maxX = Math.max(maxX, s.position.x);
    minY = Math.min(minY, s.position.y); maxY = Math.max(maxY, s.position.y);
  }
  return Math.max(maxX - minX, maxY - minY, 0);
}

export function mergeRadius(areas: TourArea[]): number {
  return Math.min(8, Math.max(2.5, siteSpan(areas) * 0.03));
}

// How far apart storeys are drawn: their real gap, or a tenth of the site
// (at least 6 m) when that would not read on a large one.
export const storeyGap = (areas: TourArea[]) => Math.max(6, siteSpan(areas) * 0.1);

export interface TourMapOptions {
  hop?: number;
  // Height of the floor under a scan; the scan's own height without one.
  floorZ?: (s: TourStation) => number;
}

function levelsOf(zs: number[]): (z: number) => number {
  const sorted = [...zs].sort((a, b) => a - b);
  const breaks: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] > FLOOR_GAP_M) breaks.push((sorted[i] + sorted[i - 1]) / 2);
  }
  return (z) => breaks.filter((b) => z > b).length;
}

// Kruskal over candidate edges: the fewest lines that still show every
// connection, instead of a hairball of every pair in ring reach.
function spanning<T extends { a: string; b: string; w: number }>(ids: string[], edges: T[]): T[] {
  const parent = new Map(ids.map((id) => [id, id]));
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(x, r);
    return r;
  };
  const out: T[] = [];
  for (const e of [...edges].sort((p, q) => p.w - q.w)) {
    const ra = find(e.a), rb = find(e.b);
    if (ra === rb) continue;
    parent.set(ra, rb);
    out.push(e);
  }
  return out;
}

// Convex hull of every node grown into a circle of `pad`, so one or two nodes
// still make a plate.
function plateOutline(points: Array<[number, number]>, pad: number): Array<[number, number]> {
  const ring: Array<[number, number]> = [];
  for (const [x, y] of points) {
    for (let k = 0; k < 12; k++) {
      const t = (k / 12) * Math.PI * 2;
      ring.push([x + Math.cos(t) * pad, y + Math.sin(t) * pad]);
    }
  }
  ring.sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number]> = [];
  for (const p of ring) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<[number, number]> = [];
  for (let i = ring.length - 1; i >= 0; i--) {
    const p = ring[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

export function buildTourMap(areas: TourArea[], link: StationLink, opts: TourMapOptions = {}): TourMap {
  const hop = opts.hop ?? AREA_HOP_M;
  const floorZ = opts.floorZ ?? ((s: TourStation) => s.position.z);
  const merge = mergeRadius(areas);
  const gap = storeyGap(areas);
  const nodes: TourMapNode[] = [];
  const nodeOf = new Map<string, string>();
  const hops: TourMapLink[] = [];
  // Drawn height of each node: its storey's base plus its true height in it.
  const drawZ = new Map<string, number>();

  for (const area of areas) {
    const floors = new Map(area.stations.map((s) => [s.id, floorZ(s)]));
    const levelOf = levelsOf([...floors.values()]);
    const levelOfStation = (s: TourStation) => levelOf(floors.get(s.id)!);
    const mine: TourMapNode[] = [];
    const nodeFloor = new Map<string, number>();
    const taken = new Set<string>();
    for (const seed of area.stations) {
      if (taken.has(seed.id)) continue;
      const level = levelOfStation(seed);
      const members = area.stations.filter((s) =>
        !taken.has(s.id) && levelOfStation(s) === level && flat(s.position, seed.position) <= merge);
      members.forEach((s) => taken.add(s.id));
      const real = { x: 0, y: 0, z: 0 };
      let floor = 0;
      for (const s of members) { real.x += s.position.x; real.y += s.position.y; real.z += s.position.z; floor += floors.get(s.id)!; }
      real.x /= members.length; real.y /= members.length; real.z /= members.length;
      const node = { id: `n${nodes.length + mine.length}`, areaKey: area.key, level, real, position: { ...real }, stationIds: members.map((s) => s.id) };
      nodeFloor.set(node.id, floor / members.length);
      mine.push(node);
      members.forEach((s) => nodeOf.set(s.id, node.id));
    }
    const lowest = new Map<number, number>();
    for (const n of mine) lowest.set(n.level, Math.min(lowest.get(n.level) ?? Infinity, nodeFloor.get(n.id)!));
    const base = new Map<number, number>();
    const levels = [...lowest.keys()].sort((a, b) => a - b);
    levels.forEach((l, i) => {
      base.set(l, i === 0 ? lowest.get(l)! : base.get(levels[i - 1])! + Math.max(lowest.get(l)! - lowest.get(levels[i - 1])!, gap));
    });
    for (const n of mine) drawZ.set(n.id, base.get(n.level)! + nodeFloor.get(n.id)! - lowest.get(n.level)!);
    const byId = new Map(mine.map((n) => [n.id, n]));
    const seen = new Set<string>();
    const candidates: Array<{ a: string; b: string; w: number }> = [];
    for (const [i, j] of linkedPairs(area.stations, link, hop)) {
      const a = nodeOf.get(area.stations[i].id)!, b = nodeOf.get(area.stations[j].id)!;
      const k = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (a === b || seen.has(k)) continue;
      seen.add(k);
      candidates.push({ a, b, w: Math.hypot(flat(byId.get(a)!.real, byId.get(b)!.real), byId.get(a)!.real.z - byId.get(b)!.real.z) });
    }
    spanning(mine.map((n) => n.id), candidates).forEach(({ a, b }) => hops.push({ a, b }));
    nodes.push(...mine);
  }

  const byArea = new Map<string, TourMapNode[]>();
  for (const n of nodes) {
    const list = byArea.get(n.areaKey);
    if (list) list.push(n); else byArea.set(n.areaKey, [n]);
  }
  const areaCandidates: Array<{ a: string; b: string; w: number; na: TourMapNode; nb: TourMapNode }> = [];
  for (let i = 0; i < areas.length; i++) {
    for (let j = i + 1; j < areas.length; j++) {
      let best: { w: number; na: TourMapNode; nb: TourMapNode } | null = null;
      for (const na of byArea.get(areas[i].key) ?? []) {
        for (const nb of byArea.get(areas[j].key) ?? []) {
          const w = Math.hypot(flat(na.real, nb.real), na.real.z - nb.real.z);
          if (!best || w < best.w) best = { w, na, nb };
        }
      }
      if (best) areaCandidates.push({ a: areas[i].key, b: areas[j].key, ...best });
    }
  }
  const areaLinks = spanning(areas.map((a) => a.key), areaCandidates);
  // The label is the walk between the two nearest scans, not between node middles.
  const stationPos = new Map<string, Vec3>();
  for (const a of areas) for (const s of a.stations) stationPos.set(s.id, s.position);
  const jumps: TourMapJump[] = areaLinks.map((l) => {
    let d = Infinity;
    for (const p of l.na.stationIds) for (const q of l.nb.stationIds) {
      const sp = stationPos.get(p)!, sq = stationPos.get(q)!;
      d = Math.min(d, Math.hypot(sp.x - sq.x, sp.y - sq.y, sp.z - sq.z));
    }
    return { a: l.na.id, b: l.nb.id, distance: d };
  });

  // Shorten the jumps: walk the area tree from the largest area and pull each
  // area along its jump until the gap is at most `cap`, keeping its direction.
  let extent = 0;
  for (const list of byArea.values()) {
    for (const p of list) for (const q of list) extent = Math.max(extent, flat(p.real, q.real));
  }
  const cap = Math.max(hop, extent * 0.5);
  const shift = new Map<string, { x: number; y: number }>();
  if (areas.length) shift.set(areas[0].key, { x: 0, y: 0 });
  const pending = [...areaLinks];
  while (pending.length) {
    const i = pending.findIndex((l) => shift.has(l.a) !== shift.has(l.b));
    if (i < 0) break;
    const l = pending.splice(i, 1)[0];
    const [from, to, nFrom, nTo] = shift.has(l.a) ? [l.a, l.b, l.na, l.nb] : [l.b, l.a, l.nb, l.na];
    const base = shift.get(from)!;
    const vx = nTo.real.x - nFrom.real.x, vy = nTo.real.y - nFrom.real.y;
    const len = Math.hypot(vx, vy);
    const pull = len > cap ? 1 - cap / len : 0;
    shift.set(to, { x: base.x - vx * pull, y: base.y - vy * pull });
  }
  for (const n of nodes) {
    const s = shift.get(n.areaKey) ?? { x: 0, y: 0 };
    n.position = { x: n.real.x + s.x, y: n.real.y + s.y, z: drawZ.get(n.id)! };
  }

  const plates: TourMapPlate[] = [];
  for (const [areaKey, list] of byArea) {
    const levels = new Map<number, TourMapNode[]>();
    for (const n of list) {
      const l = levels.get(n.level);
      if (l) l.push(n); else levels.set(n.level, [n]);
    }
    for (const [level, ns] of levels) {
      const z = Math.min(...ns.map((n) => n.position.z));
      plates.push({ areaKey, level, z, outline: plateOutline(ns.map((n) => [n.position.x, n.position.y]), merge + 1.5) });
    }
  }

  return { nodes, hops, jumps, plates, nodeOf };
}
